<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useFormDataStore } from '@/entries/interact-form/stores/formData'
import type { InputDataItem, ProcessedFormData } from '@/entries/interact-form/types'
import ThankYouView from '@/entries/interact-form/components/ThankYouView.vue'
import InitialFormView from '@/entries/interact-form/components/InitialFormView.vue'
import FormModal from '@/entries/interact-form/components/FormModal.vue'
import ContactFormWrapper from '@/entries/interact-form/components/ContactFormWrapper.vue'
import { extractPrefix, removePrefix } from '@/entries/interact-form/constants'

// Composables
import { useContactForm } from '@/entries/interact-form/composables/useContactForm'
import { useModalControl } from '@/entries/interact-form/composables/useModalControl'
import { useFormNavigation } from '@/entries/interact-form/composables/useFormNavigation'
import { useFormState } from '@/entries/interact-form/composables/useFormState'
import { useGlobalFunctions } from '@/entries/interact-form/composables/useGlobalFunctions'


// プロップの型定義（InputDataItemを再利用）
interface Props {
  contactformId: string
  contactformHtml: string
  datas: InputDataItem[]
}

const props = defineProps<Props>()
const formDataStore = useFormDataStore()

// イベントエミッター
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submit', data: ProcessedFormData): void
  (e: 'unmount'): void
}>()

// Composablesの使用
const { isFormSubmitted, displayState, setFormSubmitted, setDisplayState } = useFormState()
const { isModalOpen, openModal, closeModal } = useModalControl()

// 加工済みのContact Form 7 HTML
const processedContactFormHtml = ref('')

/**
 * モーダルを開く関数
 *
 * 最初の質問への回答状態、現在の質問インデックス、次の質問に進む関数を
 * useModalControlのopenModal関数に渡してモーダルを開きます
 */
function handleOpenModal() {
  console.log('[InteractForm] Opening modal', {
    hasAnsweredFirstQuestion: hasAnsweredFirstQuestion.value,
    currentQuestionIndex: formDataStore.currentQuestionIndex
  })
  
  openModal(
    hasAnsweredFirstQuestion.value,
    formDataStore.currentQuestionIndex,
    formDataStore.nextQuestion,
  )
}

/**
 * モーダルを閉じる関数
 *
 * フォーム送信状態をuseModalControlのcloseModal関数に渡して
 * モーダルを閉じます
 */
function handleCloseModal() {
  console.log('[InteractForm] Closing modal', {
    isFormSubmitted: isFormSubmitted.value
  })
  
  closeModal(isFormSubmitted.value)
}

const { hasAnsweredFirstQuestion, handleAnswerUpdate, handleLastQuestionSubmit } =
  useFormNavigation({
    openModalFn: handleOpenModal,
  })

const {
  injectFormDataToHtml,
  setupRequiredFieldsValidation,
  setupContactForm7EventListeners,
  checkFormSubmissionStatus,
} = useContactForm(props.contactformId, props.contactformHtml)

// グローバル関数の設定
useGlobalFunctions()

/**
 * 最終的なフォーム送信処理
 *
 * Contact Form 7の送信完了後に呼び出され、以下の処理を行います:
 * 1. フォームデータを処理して整形
 * 2. 親コンポーネントに送信イベントを発火
 * 3. フォーム送信済みフラグをセット
 */
function submitForm() {
  console.log('[InteractForm] submitForm called - Contact Form 7 submission successful')
  const processedData = formDataStore.processFormData()

  // 詳細なデバッグ情報をコンソールに出力
  console.group('ProcessedFormData デバッグ情報')
  console.log('全体データ:', processedData)
  console.log('Contact Form ID:', processedData.contactFormId)
  console.log('ページタイトル:', processedData.pageTitle)

  // トリガー設問と選択されたサービスの情報を詳細に出力
  console.group('トリガー設問情報')
  if (processedData.selectedServices.length > 0) {
    processedData.selectedServices.forEach((service, index) => {
      console.group(`トリガー設問 #${index + 1}`)
      console.log('質問ID:', service.questionId)
      console.log('質問内容:', service.question)
      console.log('選択された回答(元の値):', service.answer)
      console.log('選択された回答(表示用):', service.displayAnswer)
      console.log('回答プレフィックス:', service.answerPrefix)
      console.log('関連サービスID:', service.serviceIds)
      console.groupEnd()
    })
  } else {
    console.log('トリガー設問による選択はありません')
  }
  console.groupEnd()

  // フォーマット済みのHTML出力
  console.log('フォーマット済みHTML:', processedData.formattedHtml)
  console.groupEnd()

  // 送信イベントを発火（親コンポーネントへの通知用）
  emit('submit', processedData)

  // フォーム送信済みフラグをセット
  setFormSubmitted(true)
  // サンクスページへリダイレクト
  window.location.assign('/taiwa-thanks')
}

/**
 * 回答更新のラッパー関数
 *
 * @param {number} id - 質問のID
 * @param {string | string[]} answer - 更新する回答（文字列または文字列の配列）
 */
function handleUpdateAnswer(id: number, answer: string | string[]) {
  // 回答更新前のデバッグ情報
  const question = formDataStore.formDetails.find(item => item.id === id)

  console.group('回答更新')
  console.log('質問ID:', id)
  console.log('質問内容:', question?.question)
  console.log('トリガー設問:', question?.is_trigger)
  console.log('更新前の回答:', question?.answer)
  console.log('更新する回答:', answer)

  // トリガー設問の場合は、プレフィックスの情報も出力
  if (question?.is_trigger && typeof answer === 'string') {
    const prefix = extractPrefix(answer)
    console.log('回答プレフィックス:', prefix)
    console.log('表示用回答テキスト:', removePrefix(answer))
  }
  console.groupEnd()

  // 回答を更新
  handleAnswerUpdate(id, answer)
}

/**
 * 最後の質問の送信処理のラッパー関数
 *
 * 全ての質問に回答した後、Contact Form 7の必須フィールドの
 * バリデーションを設定します
 */
function handleSubmitLastQuestion() {
  handleLastQuestionSubmit(setupRequiredFieldsValidation)
}

/**
 * サンクスビューを表示するかどうかの計算プロパティ
 *
 * 以下の条件のいずれかを満たす場合にtrueを返します:
 * - 表示状態が'thank-you'
 * - 表示状態が'full'かつフォームが送信済み
 */
const showThankYouView = computed(
  () =>
    displayState.value === 'thank-you' || (displayState.value === 'full' && isFormSubmitted.value),
)

/**
 * 初期フォームビューを表示するかどうかの計算プロパティ
 *
 * 以下の条件をすべて満たす場合にtrueを返します:
 * - 表示状態が'full'
 * - フォームが未送信
 * - 質問が1つ以上ある
 * - モーダルが閉じているか、最初の質問に未回答
 */
const showInitialFormView = computed(
  () =>
    displayState.value === 'full' &&
    !isFormSubmitted.value &&
    formDataStore.totalQuestions > 0 &&
    (!isModalOpen.value || !hasAnsweredFirstQuestion.value),
)

/**
 * フォームモーダルを表示するかどうかの計算プロパティ
 *
 * 以下の条件をすべて満たす場合にtrueを返します:
 * - モーダルが開いている
 * - 最初の質問に回答済み
 * - フォームが未送信
 */
const showFormModal = computed(
  () => isModalOpen.value && hasAnsweredFirstQuestion.value && !isFormSubmitted.value,
)

/**
 * コンポーネントがマウントされたときの初期化処理
 *
 * 以下の処理を行います:
 * 1. URLのハッシュを確認し、Contact Form 7のフラグメントがあれば感謝ページへリダイレクト
 * 2. 質問データの存在確認
 * 3. ストアのデータ初期化
 * 4. Contact Form 7のHTMLを加工
 * 5. Contact Form 7のイベントリスナー設定
 * 6. フォーム送信状態の確認
 */
onMounted(() => {
  // Contact Form 7のフラグメントがあればサンクスページへリダイレクト
  if (location.hash && location.hash.startsWith('#wpcf7-f')) {
    window.location.replace('/taiwa-thanks')
    return
  }
  console.log('InteractForm component mounted')
  console.log('Props received:', props.datas)
  console.log('Contact Form ID:', props.contactformId)
  console.log('Contact Form HTML:', props.contactformHtml ? 'HTML設定済み' : '未設定')

  try {
    // マウント時にURLを確認し、Contact Form 7のフラグメントがあればthanksページへ遷移
    // データが正しく渡されているか確認
    if (!props.datas || props.datas.length === 0) {
      throw new Error('No question data provided!')
    }

    // ストアにデータ初期化を委譲（contactformIdも一緒に渡す）
    const initResult = formDataStore.initializeFromInputData(props.datas, props.contactformId)

    // 初期化結果のデバッグ出力
    console.group('フォームデータ初期化結果')
    console.log('初期化結果:', initResult)
    console.log('フォーム詳細:', formDataStore.formDetails)
    console.log('トリガー設問:', formDataStore.formDetails.filter(item => item.is_trigger))
    console.groupEnd()

    // Contact Form 7のHTMLを加工
    if (props.contactformHtml) {
      processedContactFormHtml.value = injectFormDataToHtml(props.contactformHtml, formDataStore)
    }

    // Contact Form 7のイベントリスナーを設定
    setupContactForm7EventListeners(submitForm)

    // マウント時にフォームの送信状態を確認
    // DOMが完全にレンダリングされた後に実行するため、少し遅延させる
    setTimeout(() => {
      checkFormSubmissionStatus(props.contactformId)
    }, 500)
  } catch (error) {
    console.error('Failed to initialize InteractForm:', error)
    // エラー状態を設定
    setDisplayState('none')
  }
})

/**
 * フォームデータの変更を監視し、HTMLを再生成する
 *
 * formDataStore.formDetailsが変更されたときに、
 * Contact Form 7のHTMLを再加工します。
 * deep: trueオプションで、ネストされたオブジェクトの変更も検知します。
 */
watch(
  () => formDataStore.formDetails,
  () => {
    if (props.contactformHtml) {
      processedContactFormHtml.value = injectFormDataToHtml(props.contactformHtml, formDataStore)
    }
  },
  { deep: true },
)
</script>

<template>
  <!-- 表示状態に応じて条件分岐（計算プロパティを使用） -->
  <ThankYouView v-if="showThankYouView" />

  <!-- 通常表示（モーダルが閉じられている場合の表示） -->
  <InitialFormView v-else-if="showInitialFormView"
    :hasAnsweredFirstQuestion="hasAnsweredFirstQuestion" :isModalOpen="isModalOpen"
    @openModal="handleOpenModal" @updateAnswer="handleUpdateAnswer"
    @lastQuestionSubmit="handleSubmitLastQuestion" />

  <!-- モーダルコンポーネント -->
  <FormModal v-if="showFormModal" :isOpen="showFormModal" :contactformHtml="props.contactformHtml"
    :contactformId="props.contactformId"
    @close="handleCloseModal" @updateAnswer="handleUpdateAnswer"
    @lastQuestionSubmit="handleSubmitLastQuestion">
    <template #contact-form>
      <ContactFormWrapper :contactformHtml="props.contactformHtml"
        :processedHtml="processedContactFormHtml" />
    </template>
  </FormModal>
</template>
